import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Building2, Check, X, Loader2, HelpCircle, Info, Sparkles } from 'lucide-react';
import axios from 'axios';
import { getToken } from '../../services/tokenService';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * SiretInput - Composant de saisie SIRET Ultra-Professionnel High-Tech
 * 
 * Features:
 * - Validation Luhn en temps réel
 * - Validation API backend avec support INSEE
 * - Auto-complétion des données entreprise (si onAutoComplete fourni)
 * - Formatage automatique (XXX XXX XXX XXXXX)
 * - Bulle d'aide contextuelle
 * - Indicateurs visuels animés
 * - Compteur de caractères interactif
 */
export default function SiretInput({ 
  value, 
  onChange, 
  placeholder = 'Entrez le SIRET', 
  required = false, 
  className = '',
  label = 'N° SIRET',
  showLabel = true,
  onAutoComplete = null,  // Callback pour auto-complétion des données entreprise
  onValidation = null     // Callback pour résultat de validation
}) {
  const [siret, setSiret] = useState('');
  const [isValid, setIsValid] = useState(null);
  const [validating, setValidating] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [_companyData, setCompanyData] = useState(null);
  const initializedRef = useRef(false);

  // Luhn algorithm for SIRET validation
  const luhnCheck = useCallback((num) => {
    let sum = 0;
    for (let i = 0; i < num.length; i++) {
      let digit = parseInt(num[i], 10);
      if ((num.length - i) % 2 === 0) {
        digit *= 2;
        if (digit > 9) digit -= 9;
      }
      sum += digit;
    }
    return sum % 10 === 0;
  }, []);

  // Validation via API backend
  const validateSiretApi = useCallback(async (siretNumber) => {
    setValidating(true);
    setErrorMessage('');
    
    // Validation locale d'abord
    if (siretNumber.length !== 14) {
      setIsValid(false);
      setErrorMessage('Le SIRET doit contenir exactement 14 chiffres');
      setValidating(false);
      if (onValidation) onValidation({ isValid: false, message: 'Le SIRET doit contenir exactement 14 chiffres' });
      return;
    }

    if (!/^\d{14}$/.test(siretNumber)) {
      setIsValid(false);
      setErrorMessage('Le SIRET ne doit contenir que des chiffres');
      setValidating(false);
      if (onValidation) onValidation({ isValid: false, message: 'Le SIRET ne doit contenir que des chiffres' });
      return;
    }

    if (!luhnCheck(siretNumber)) {
      setIsValid(false);
      setErrorMessage('SIRET invalide (erreur de vérification)');
      setValidating(false);
      if (onValidation) onValidation({ isValid: false, message: 'SIRET invalide (erreur de vérification)' });
      return;
    }

    // Appel API pour validation avancée et auto-complétion
    try {
      const token = getToken();
      const response = await axios.get(`${API}/siret/validate/${siretNumber}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      const data = response.data;
      setIsValid(data.format_valid);
      setErrorMessage(data.format_valid ? '' : data.validation_message);
      setCompanyData(data.company_data);
      
      // Callback de validation
      if (onValidation) {
        onValidation({
          isValid: data.format_valid,
          message: data.validation_message,
          companyData: data.company_data
        });
      }
      
      // Auto-complétion si données disponibles
      if (data.company_data && onAutoComplete) {
        onAutoComplete(data.company_data);
      }
      
    } catch {
      // Fallback sur validation locale en cas d'erreur API
      setIsValid(true);
      setErrorMessage('');
      if (onValidation) onValidation({ isValid: true, message: 'Format SIRET valide' });
    }
    
    setValidating(false);
  }, [luhnCheck, onValidation, onAutoComplete]);

  const validateSiret = useCallback((siretNumber) => {
    validateSiretApi(siretNumber);
  }, [validateSiretApi]);

  useEffect(() => {
    if (value && !initializedRef.current) {
      initializedRef.current = true;
      const cleaned = value.replace(/\D/g, '');
      setSiret(cleaned);
      if (cleaned.length === 14) {
        validateSiret(cleaned);
      }
    }
  }, [value, validateSiret]);

  const handleChange = (e) => {
    const input = e.target.value.replace(/\D/g, '');
    const trimmed = input.slice(0, 14);
    setSiret(trimmed);
    setIsValid(null);
    setErrorMessage('');
    
    onChange(trimmed);

    if (trimmed.length === 14) {
      validateSiret(trimmed);
    }
  };

  const formatSiret = (siretNumber) => {
    if (!siretNumber) return '';
    const cleaned = siretNumber.replace(/\D/g, '');
    const parts = [
      cleaned.slice(0, 3),
      cleaned.slice(3, 6),
      cleaned.slice(6, 9),
      cleaned.slice(9, 14)
    ].filter(Boolean);
    return parts.join(' ');
  };

  // Calculer la progression
  const progress = (siret.length / 14) * 100;

  // Déterminer les couleurs selon l'état
  const getBorderColor = () => {
    if (isValid === true) return 'border-emerald-500 ring-2 ring-emerald-500/30';
    if (isValid === false) return 'border-red-500 ring-2 ring-red-500/30';
    if (isFocused) return 'border-blue-500 ring-2 ring-blue-500/30';
    return 'border-slate-200 hover:border-slate-300';
  };

  const getProgressColor = () => {
    if (isValid === true) return 'bg-gradient-to-r from-emerald-500 to-teal-500';
    if (isValid === false) return 'bg-gradient-to-r from-red-500 to-orange-500';
    if (siret.length > 0) return 'bg-gradient-to-r from-blue-500 to-purple-500';
    return 'bg-slate-300';
  };

  return (
    <div className={`space-y-2 ${className}`}>
      {/* Label avec bulle d'aide */}
      {showLabel && (
        <div className="flex items-center justify-between">
          <label className="text-sm font-semibold text-slate-700 flex items-center gap-1.5">
            <Building2 className="h-4 w-4 text-slate-500" />
            {label}
            {required && <span className="text-red-500">*</span>}
          </label>
          
          {/* Bulle d'aide */}
          <div className="relative">
            <button
              type="button"
              className="p-1.5 rounded-full hover:bg-blue-50 transition-all duration-200 group"
              onMouseEnter={() => setShowTooltip(true)}
              onMouseLeave={() => setShowTooltip(false)}
              onClick={() => setShowTooltip(!showTooltip)}
            >
              <HelpCircle className="h-4 w-4 text-slate-400 group-hover:text-blue-500 transition-colors" />
            </button>
            
            {/* Tooltip */}
            {showTooltip && (
              <div className="absolute z-50 right-0 bottom-full mb-2 animate-in fade-in slide-in-from-bottom-2 duration-200">
                <div className="bg-slate-800 text-white text-sm px-4 py-3 rounded-xl shadow-2xl w-72 border border-slate-700">
                  <div className="flex items-start gap-2.5">
                    <Info className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-blue-300 mb-2">Comment trouver votre SIRET ?</p>
                      <ul className="text-slate-300 text-xs space-y-1.5">
                        <li className="flex items-start gap-1.5">
                          <span className="text-emerald-400">•</span>
                          <span>Sur votre extrait <strong>Kbis</strong></span>
                        </li>
                        <li className="flex items-start gap-1.5">
                          <span className="text-emerald-400">•</span>
                          <span>Sur vos <strong>factures</strong> ou documents officiels</span>
                        </li>
                        <li className="flex items-start gap-1.5">
                          <span className="text-emerald-400">•</span>
                          <span>Sur <strong>societe.com</strong> ou <strong>infogreffe.fr</strong></span>
                        </li>
                      </ul>
                      <div className="mt-3 pt-2 border-t border-slate-600">
                        <p className="text-amber-300 text-xs font-medium flex items-center gap-1">
                          <Sparkles className="h-3 w-3" />
                          Exactement 14 chiffres : SIREN (9) + NIC (5)
                        </p>
                      </div>
                    </div>
                  </div>
                  {/* Flèche */}
                  <div className="absolute right-4 -bottom-2 w-3 h-3 bg-slate-800 transform rotate-45 border-r border-b border-slate-700"></div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Champ de saisie */}
      <div className="relative">
        <div className={`
          flex items-center rounded-xl border-2 transition-all duration-300 ease-out
          bg-white/80 backdrop-blur-sm overflow-hidden
          ${getBorderColor()}
        `}>
          {/* Icône */}
          <div className={`
            flex items-center justify-center px-4 py-3.5 
            border-r transition-colors duration-300
            ${isValid === true ? 'bg-emerald-50 border-emerald-200' : 
              isValid === false ? 'bg-red-50 border-red-200' : 
              'bg-slate-50 border-slate-200'}
          `}>
            <Building2 className={`h-5 w-5 transition-colors duration-300 ${
              isValid === true ? 'text-emerald-600' : 
              isValid === false ? 'text-red-500' : 
              'text-slate-500'
            }`} />
          </div>

          {/* Input */}
          <input
            type="text"
            value={formatSiret(siret)}
            onChange={handleChange}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            placeholder={siret.length === 0 ? "XXX XXX XXX XXXXX" : placeholder}
            required={required}
            className="flex-1 px-4 py-3.5 outline-none text-base font-mono tracking-wider bg-transparent placeholder:text-slate-300"
            maxLength={17}
          />

          {/* Compteur animé */}
          <div className={`
            px-3 py-1 mx-2 rounded-lg text-xs font-bold font-mono transition-all duration-300
            ${siret.length === 14 
              ? 'bg-emerald-100 text-emerald-700' 
              : siret.length > 0 
                ? 'bg-blue-100 text-blue-700' 
                : 'bg-slate-100 text-slate-500'}
          `}>
            {siret.length}/14
          </div>

          {/* Icône de statut */}
          <div className="pr-4">
            {validating && (
              <div className="animate-spin">
                <Loader2 className="h-5 w-5 text-blue-500" />
              </div>
            )}
            {!validating && isValid === true && (
              <div className="animate-in zoom-in duration-300">
                <div className="bg-emerald-500 rounded-full p-1">
                  <Check className="h-4 w-4 text-white" />
                </div>
              </div>
            )}
            {!validating && isValid === false && (
              <div className="animate-in zoom-in duration-300">
                <div className="bg-red-500 rounded-full p-1">
                  <X className="h-4 w-4 text-white" />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Barre de progression */}
        <div className="absolute -bottom-0.5 left-0 right-0 h-1 bg-slate-100 rounded-full overflow-hidden">
          <div 
            className={`h-full transition-all duration-500 ease-out rounded-full ${getProgressColor()}`}
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Messages de statut */}
      <div className="flex items-center justify-between min-h-[20px]">
        <div className="flex items-center gap-2">
          {siret.length === 0 && required && (
            <p className="text-xs text-amber-600 flex items-center gap-1 animate-in fade-in duration-300">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400"></span>
              Champ obligatoire - 14 chiffres attendus
            </p>
          )}
          {siret.length > 0 && siret.length < 14 && (
            <p className="text-xs text-blue-600 flex items-center gap-1 animate-in fade-in duration-300">
              <Loader2 className="h-3 w-3 animate-spin" />
              Encore {14 - siret.length} chiffre{14 - siret.length > 1 ? 's' : ''} à saisir...
            </p>
          )}
          {errorMessage && (
            <p className="text-xs text-red-600 font-medium flex items-center gap-1 animate-in slide-in-from-left duration-300">
              <X className="h-3 w-3" />
              {errorMessage}
            </p>
          )}
          {isValid === true && (
            <p className="text-xs text-emerald-600 font-semibold flex items-center gap-1 animate-in slide-in-from-left duration-300">
              <Check className="h-3 w-3" />
              SIRET valide et vérifié
            </p>
          )}
        </div>

        {/* Structure SIRET */}
        {siret.length > 0 && (
          <div className="text-xs text-slate-400 font-mono">
            <span className={siret.length >= 9 ? 'text-emerald-500' : ''}>SIREN</span>
            <span className="mx-1">+</span>
            <span className={siret.length === 14 ? 'text-emerald-500' : ''}>NIC</span>
          </div>
        )}
      </div>
    </div>
  );
}
